// client/src/coms/voip/voiceclient_parts/sfx.js
import { audioElsByTrackSid, voiceState } from './state';

export function ensureSfxCtx() {
  if (voiceState.sfxCtx) return voiceState.sfxCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    voiceState.sfxCtx = new Ctx();
    return voiceState.sfxCtx;
  } catch {
    return null;
  }
}

export function ensureGateCtx() {
  if (voiceState.gateCtx) return voiceState.gateCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    voiceState.gateCtx = new Ctx();
    return voiceState.gateCtx;
  } catch {
    return null;
  }
}

export function primeGateCtx() {
  const ctx = ensureGateCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
  } catch {}
}

export function primeSfxCtx() {
  const ctx = ensureSfxCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
  } catch {}
}

function buildChimeChain(ctx, t0, opts) {
  const out = ctx.createGain();
  out.gain.setValueAtTime(0.0001, t0);
  out.gain.exponentialRampToValueAtTime(opts?.gainPeak || 0.16, t0 + (opts?.gainAttack || 0.02));
  out.gain.exponentialRampToValueAtTime(0.0001, t0 + (opts?.gainReleaseAt || 0.95));

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.setValueAtTime(opts?.hp || 180, t0);

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(opts?.lp || 5200, t0);

  const delay = ctx.createDelay(1.0);
  delay.delayTime.setValueAtTime(opts?.delayTime || 0.12, t0);

  const fb = ctx.createGain();
  fb.gain.setValueAtTime(opts?.feedback || 0.22, t0);

  const wet = ctx.createGain();
  wet.gain.setValueAtTime(opts?.wet || 0.22, t0);

  const dry = ctx.createGain();
  dry.gain.setValueAtTime(opts?.dry || 0.95, t0);

  hp.connect(lp);
  lp.connect(dry);
  dry.connect(out);

  lp.connect(delay);
  delay.connect(fb);
  fb.connect(delay);
  delay.connect(wet);
  wet.connect(out);

  out.connect(ctx.destination);

  return { out, hp, lp };
}

function spawnVoice(ctx, dest, freq, start, dur, type, brightness, amp) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  const f = ctx.createBiquadFilter();

  osc.type = type;

  f.type = 'lowpass';
  f.frequency.setValueAtTime(brightness, start);

  const a = 0.018;
  const r = 0.22;

  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(amp, start + a);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur + r);

  osc.frequency.setValueAtTime(freq, start);
  osc.connect(f);
  f.connect(g);
  g.connect(dest);

  try {
    osc.start(start);
    osc.stop(start + dur + r + 0.02);
  } catch {}
}

function spawnSparkle(ctx, dest, t0, when, strength, centerHz) {
  try {
    const noiseDur = 0.035;
    const noiseBuf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * noiseDur)), ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;

    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t0 + when);
    ng.gain.exponentialRampToValueAtTime(strength, t0 + when + 0.015);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + when + 0.06);

    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.setValueAtTime(centerHz, t0 + when);
    nf.Q.setValueAtTime(10, t0 + when);

    noise.connect(nf);
    nf.connect(ng);
    ng.connect(dest);

    noise.start(t0 + when);
    noise.stop(t0 + when + 0.07);
  } catch {}
}

export function playJoinChime() {
  const now = Date.now();
  if (now - voiceState.joinSfxLastAt < 450) return;
  voiceState.joinSfxLastAt = now;

  const ctx = ensureSfxCtx();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch {}

  let t0;
  try {
    t0 = ctx.currentTime;
  } catch {
    return;
  }

  const chain = buildChimeChain(ctx, t0, {
    gainPeak: 0.16,
    gainAttack: 0.02,
    gainReleaseAt: 0.95,
    hp: 180,
    lp: 5200,
    delayTime: 0.12,
    feedback: 0.22,
    wet: 0.22,
    dry: 0.95,
  });

  spawnVoice(ctx, chain.hp, 523.25, t0 + 0.0, 0.16, 'triangle', 4200, 0.85);
  spawnVoice(ctx, chain.hp, 659.25, t0 + 0.09, 0.16, 'triangle', 4500, 0.82);
  spawnVoice(ctx, chain.hp, 783.99, t0 + 0.18, 0.18, 'sine', 5200, 0.78);
  spawnVoice(ctx, chain.hp, 1046.5, t0 + 0.3, 0.22, 'sine', 5600, 0.75);

  spawnSparkle(ctx, chain.hp, t0, 0.27, 0.1, 4200);

  try {
    setTimeout(() => {
      try {
        chain.out.disconnect();
      } catch {}
    }, 1500);
  } catch {}
}

export function playLeaveChime() {
  const now = Date.now();
  if (now - voiceState.leaveSfxLastAt < 450) return;
  voiceState.leaveSfxLastAt = now;

  const ctx = ensureSfxCtx();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch {}

  let t0;
  try {
    t0 = ctx.currentTime;
  } catch {
    return;
  }

  const chain = buildChimeChain(ctx, t0, {
    gainPeak: 0.14,
    gainAttack: 0.02,
    gainReleaseAt: 0.9,
    hp: 160,
    lp: 4800,
    delayTime: 0.12,
    feedback: 0.18,
    wet: 0.18,
    dry: 0.95,
  });

  spawnVoice(ctx, chain.hp, 783.99, t0 + 0.0, 0.14, 'triangle', 4200, 0.82);
  spawnVoice(ctx, chain.hp, 659.25, t0 + 0.08, 0.14, 'triangle', 4000, 0.78);
  spawnVoice(ctx, chain.hp, 523.25, t0 + 0.16, 0.16, 'sine', 3600, 0.74);

  try {
    setTimeout(() => {
      try {
        chain.out.disconnect();
      } catch {}
    }, 1400);
  } catch {}
}

export function ensurePlayRetryHook() {
  if (voiceState.pendingPlay) return;
  voiceState.pendingPlay = true;

  const onAny = () => {
    voiceState.pendingPlay = false;

    for (const [, el] of audioElsByTrackSid.entries()) {
      if (!el) continue;
      try {
        el.play().catch(() => {});
      } catch {}
    }

    try {
      window.removeEventListener('pointerdown', onAny);
    } catch {}
    try {
      window.removeEventListener('keydown', onAny);
    } catch {}
  };

  try {
    window.addEventListener('pointerdown', onAny, { passive: true });
  } catch {}
  try {
    window.addEventListener('keydown', onAny, { passive: true });
  } catch {}
}